/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import {Box} from "@mui/material";
import {usePostRequest} from "../../../../api/helper";
import toast from "react-hot-toast";

type ImageHistory = {
  [key: string]: {
    [imageKey: string]: string;
  };
};

const ChangeHistory = (props: any) => {
  const [text, setText] = useState<any>([]);
  const [ownersList, setOwnersList] = useState<any>([]);
  const [userNameData, setUserNameData] = useState<any>("");

  const fetchHandle = usePostRequest("/HttpTriggerGetDataCoreSQL?container=Users");

  useEffect(() => {
    fetchData();
  }, []);

  const getFullNameById = (gmid: any, data: any) => {
    const item = data.find((obj: { id: any }) => obj.id === gmid);
    return item ? `${item.firstName} ${item.lastName}` : "Name not found";
  };

  const fetchData = () => {
    fetchHandle()
      .then((data: any) => {
        setOwnersList(data);
      })
      .catch((e: any) => {
        toast.error("Error querying the database");
        console.log(e);
      });
  };

  useEffect(() => {
    if (props.object && props.object.changeHistory) {
      const {changeHistory} = props.object;
      setText(changeHistory);
    } else {
      setText([]);
    }
  }, [props]);

  // Helper function to convert camelCase to spaced and capitalized text
  const convertCamelToSpaced = (key: string) => {
    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Recursive function to handle nested objects
  const renderFields = (data: any) => {
  return Object.entries(data || {}).map(([key, value], idx) => {
    if (key === "id") {
      return null; // Skip rendering if the key is "id"
    }
    if (typeof value === "object" && value !== null) {
      const childFields = renderFields(value);
      if (!childFields.some((field) => field !== null)) {
        return null; // Skip if there are no child keys
      }
      return (
        <li key={idx}>
          <strong>{convertCamelToSpaced(key)}:</strong>
          <ul>{childFields}</ul>
        </li>
      );
    }
    if (value === null || value === undefined || value === "") {
      return null; // Skip if the value is empty
    }
    return (
      <li key={idx}>
        <strong>{convertCamelToSpaced(key)}:</strong> {String(value)}
      </li>
    );
  });
};

  // Function to render image history
  const renderImageHistory = (imageHistory: ImageHistory | undefined) => {
    if (!imageHistory || Object.keys(imageHistory).length === 0) return null;

    const hasChanges = Object.values(imageHistory).some(
      (details) => typeof details === "object" && Object.keys(details).length > 0
    );

    if (!hasChanges) return null;

    return (
      <div>
        <p>
          <strong>Images Changed:</strong>
        </p>
        <ul>
          {Object.entries(imageHistory).map(([index, details], i) => {
            const [imageKey, imageValue] = Object.entries(details)[0];
            return (
              <li key={i}>
                {`${imageKey} ${imageValue}`}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // Function to format the timestamp using native JavaScript
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString(); // Formats to readable date and time
  };

  const renderSignVariantsChanges = (signVariants: any) => {
    if (!signVariants || signVariants.length === 0) return null;

    const sizeMap = props?.object?.signSizes ? new Map(props?.object?.signSizes.map((size: { sizeID: any; }) => [size.sizeID, size])) : new Map();
    const speedMap = props?.object?.usageSpeeds ? new Map(props?.object?.usageSpeeds.map((speed: { speedID: any; }) => [speed.speedID, speed])) : new Map();

    const signVariantsModified = signVariants.map((variant: any) => {
      if (variant.new_items) {
        // Fully map new items with size and speed details
        const updatedNewItems = variant.new_items.map((item: { sizeID: unknown; usageSpeedID: unknown; }) => {
          const sizeDetails = sizeMap.get(item.sizeID) || {};
          const speedDetails = speedMap.get(item.usageSpeedID) || {};
          return {...item, ...sizeDetails, ...speedDetails};
        });
        return {new_items: updatedNewItems};
      } else {
        // Retrieve and merge full details for the existing variant object
        const sizeDetails = sizeMap.get(variant.sizeID) || {};
        const speedDetails = speedMap.get(variant.usageSpeedID) || {};
        return {
          ...variant,  // keep original variant details
          ...sizeDetails,  // add all size details, without duplicating sizeID
          ...speedDetails  // add all speed details, without duplicating usageSpeedID
        };
      }
    });

    return (
      <ul>
        {signVariantsModified.map((change: any, idx: number) => {
          if (change.new_items) {
            // Render details for newly added items
            return change.new_items.map((newItem: any, i: number) => (
              <li key={`new-${i}`}>
                Sign variant was added:
                <ul>{renderFields(newItem)}</ul>
              </li>
            ));
          } else if (change.deleted_items) {
            // Render details for deleted items
            return change.deleted_items.map((deletedItem: any, i: number) => (
              <li key={`new-${i}`}>
                Sign variant was deleted:
                <ul>{renderFields(deletedItem)}</ul>
              </li>
            ));
          } else {
            // Render full details for existing items
            // Filter out entries where the value only contains the "id" key
            const filteredChange = Object.fromEntries(
              Object.entries(change).filter(
                ([key, value]) =>
                  !(typeof value === "object" && value !== null && Object.keys(value).length === 1 && "id" in value)
              )
            );

            if (Object.keys(filteredChange).length === 0) {
              return null; // Skip rendering if only "id" is present
            }
            return (
              <li key={idx}>
                Row {idx + 1} in Sign Variants table was changed:
                <ul>{renderFields(change)}</ul>
              </li>
            );
          }
        })}
      </ul>
    );
  };

  return (
    <Box p={3}>
      <div>
        {Array.isArray(text) && text.length > 0 ? (
          text.map((log, index) => (
            <div key={index} style={{borderBottom: "1px solid black", padding: "10px"}}>
              {/* Display top-level required fields */}
              <p><strong>Version:</strong> {log.latestVersion}</p>
              {log.previousVersion && <p><strong>Created from Version:</strong> {log.previousVersion}</p>}
              <p><strong>Date/Time Stamp:</strong> {formatTimestamp(log.timeCreated)}</p>
              <p>
                <strong>Author:</strong> {log.author ? getFullNameById(log.author, ownersList) : "Author not available"}
              </p>

              {/* Display Fields from Content Box if changed*/}
              <p><strong>Changes Made Since Last Version:</strong></p>
              {log.description && <p><strong>Description:</strong> {log.description}</p>}
              {log.axisDefinition && <p><strong>Axis Definition:</strong> {log.axisDefinition}</p>}
              {log.comments && <p><strong>Comments:</strong> {log.comments}</p>}
              {log.references && <p><strong>References:</strong> {log.references}</p>}

              {/*Sign uses requestSource, others use objectRequestSource*/}
              {log.objectRequestSource && <p><strong>Object Request Source:</strong> {log.objectRequestSource}</p>}
              {log.requestSource && <p><strong>Object Request Source:</strong> {log.requestSource}</p>}

              {log.objectClassification && <p><strong>Object Classification:</strong> {log.objectClassification}</p>}

              {/*Sign Type*/}
              {log.signType && <p><strong>Sign Type:</strong> {log.signType}</p>}
              {/*Known Official Sign Designations*/}
              {log.knownOfficialSign && <p><strong>Known Official Sign Designations:</strong> {log.knownOfficialSign}</p>}

              {/*TODO: These multiselect fields arent working because they just show the whole field rather than the diff*/}
              {/*Message Type*/}
              {/*{log.messageType && <p><strong>Message Type:</strong> {log.messageType}</p>}*/}
              {/*Sign Shape*/}
              {/*{log.signShape && <p><strong>Sign Shape:</strong> {log.signShape}</p>}*/}
              {/*Sign Color*/}
              {/*{log.knownSignColor && <p><strong>Sign Color:</strong> {log.knownSignColor}</p>}*/}

              {/*Roar*/}
              {log.roarId && <p><strong>ROAR ID:</strong> {log.roarId}</p>}
              {log.roarUrl && <p><strong>ROAR URL:</strong> {log.roarUrl}</p>}

              {/* Display fields updated under "parameters" if changed */}
              {Object.keys(log?.parameters || {}).length > 0 ? (
                <ul>{renderFields(log.parameters)}</ul>
              ) : null}

              {/* Display image history if available */}
              {renderImageHistory(log.imageHistory)}

              {/* Display sign variants changes */}
              {props.isSignType && log.signVariants && log.signVariants.length > 0 && (
                <div>
                  <p><strong>Sign Variants Changes:</strong></p>
                  {renderSignVariantsChanges(log.signVariants)}
                </div>
              )}

            </div>
          ))
        ) : null}
      </div>
    </Box>
  );
};

export default ChangeHistory;
